import { useLocation, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo, createRef, useRef } from 'react';
import {
	FormContainer,
	DataForm,
	PageContainerActionParamType,
	Loader,
	DataFormFieldChangeParamType,
	SaveDataParamType,
	useStateContext,
} from "@vegi.toenlioe/novitate-react-components";
import Customer from '../../../models/Customer';
import { validate as uuidValidate } from "uuid";
import GroupCust from '../../../models/GroupCust';
import { MenuItems } from '../../../utils/Menu';

const groupName: string = "Servis";
const subgroupName: string = "Master";
const code: string = "SERVICE-CUSTOMER";
const title: string = "Customer";
const endpoint = "customer";

function Form() {
	const location = useLocation();
    const {authState, setAuthState, apiCaller} = useStateContext();
    const [loading, setLoading] = useState(false);
	const [formData, setFormData] = useState<Customer>({is_new: true});
	const navigate = useNavigate();
	const groupCustRef = useRef();

    const isViewMode: boolean = useMemo(() => location?.state?.isViewMode ?? false, [location]);

	useEffect(() => {
		if (!location?.state?.ucode)
			return;

        setLoading(true);

		apiCaller?.get(`${endpoint}/id?ucode=${location.state.ucode}`)
            .then((response) => {
				const data: Customer | null = response.data?.data ?
				{
					...response.data?.data,
					group_cust: response.data?.data?.ucode_group_cust ? { ucode: response.data?.data?.ucode_group_cust, kode: response.data?.data?.kode_group_cust, nama: response.data?.data?.nama_group_cust } : undefined,
					is_new: false,
				} : null;
				
				setFormData(data? {...data} : {is_new: true});
			})
            .catch((error) => console.error({error}))
            .finally(() => {
				setLoading(false);				
			})
        ;
	}, [location.state]);

    const onChanged = useCallback((valueObj: any) => {
        const {data, fieldName, value, isObject, keyFieldName, name} = valueObj;

        if (isObject && name && keyFieldName)
		{
            setFormData({
                ...formData,
                [fieldName]: value ? value[keyFieldName] : null,
                [name]: value,
            });
			return;
		}

		setFormData({
            ...data,
            [fieldName]: value,
        });
    }, [formData]);

    const onValidate = useCallback((data: Customer | undefined) => {
        if (!data?.nama)
            return false;

        if (data?.ucode && !data.ucode)
            return false;

        return true;
    }, []);

    const onClose = useCallback((pathObject: PageContainerActionParamType | null | undefined) => {
        navigate(`${pathObject?.mainPath}${pathObject?.path}`);
    }, [navigate]);

    const onSave = useCallback((param: SaveDataParamType<Customer> | undefined) => {
        if (!param?.data || !apiCaller)
            return;

        setLoading(true);
		console.log({dataToSend: param.data});

        const dataToSend = {
            ucode: param.data.ucode,
			kode: param.data.kode,
            nama: param.data.nama,
			npwp: param.data.npwp,
            ucode_group_cust: param.data.ucode_group_cust,
            alamat: param.data.alamat,
			alamat_kirim: param.data.alamat_kirim,
			telp: param.data.telp,
			jatuh_tempo: param.data.jatuh_tempo,
			keterangan: param.data.keterangan,
			aktif: param.data.aktif,
        };

        const apiCallerObj = param.data.ucode ? apiCaller.put(endpoint, JSON.stringify(dataToSend)) : apiCaller.post(endpoint, JSON.stringify(dataToSend));

        apiCallerObj
            .then(() => {
                setLoading(false);

                if (param.callback)
                    param.callback();
            })
            .catch((error) => {
                console.error({error});
                setLoading(false);
            })
        ;
    }, [apiCaller]);

    return (
		<>
        {
            loading && <Loader />
        }
			<FormContainer
				groupName={groupName}
				subgroupName={subgroupName}
				code={code}
				title={title}
                editMode={formData?.ucode !== null && uuidValidate(formData?.ucode)}
                isViewMode={isViewMode}
			>
				<DataForm<Customer>
					menuItems={MenuItems}
					groupName={groupName}
					subgroupName={subgroupName}
					code={code}
					title={title}
					data={formData}
					isViewMode={isViewMode}
					fields={[
						{
							name: "kode",
							title: "Kode Customer",
							inputType: "text",
							dataType: (typeof formData.kode),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Customer>) => onChanged(valueObj),
						},
						{
							name: "nama",
							title: "Nama Customer",
							inputType: "text",
							dataType: (typeof formData.nama),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Customer>) => onChanged(valueObj),
						},
						{
							name: "npwp",
							title: "NPWP",
							inputType: "text",
							dataType: (typeof formData.npwp),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Customer>) => onChanged(valueObj),
						},
						{
							name: "group_cust",
							keyField: "ucode",
							labelField: "nama",
							editKeyField: "ucode_group_cust",
							title: "Group Customer",
							inputType: "combobox",
							dataType: (typeof GroupCust),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Customer>) => onChanged(valueObj),
							searchEndpoint: "groupcust/nama",
							searchEndpointFilterParamName: "nama",
							ref: groupCustRef,
						},
						{
							name: "alamat",
							title: "Alamat",
							inputType: "textarea",
							dataType: (typeof formData.alamat),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Customer>) => onChanged(valueObj),
							rows: 3,
						},
						{
							name: "alamat_kirim",
							title: "Alamat Kirim",
							inputType: "textarea",
							dataType: (typeof formData.alamat_kirim),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Customer>) => onChanged(valueObj),
							rows: 3,
						},
						{
							name: "telp",
							title: "No. Telp",
							inputType: "text",
							dataType: (typeof formData.telp),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Customer>) => onChanged(valueObj),
						},
						{
							name: "jatuh_tempo",
							title: "Jatuh Tempo",
							inputType: "text",
							dataType: (typeof formData.jatuh_tempo),
							isReadonly: false,
							isRequired: true,
							onChanged: (valueObj: DataFormFieldChangeParamType<Customer>) => onChanged(valueObj),
						},
						{
							name: "keterangan",
							title: "Keterangan",
							inputType: "textarea",
							dataType: (typeof formData.keterangan),
							isReadonly: false,
							isRequired: false,
							onChanged: (valueObj: DataFormFieldChangeParamType<Customer>) => onChanged(valueObj),
							rows: 4,
						},
					]}
					onValidate={onValidate}
					onClose={(pathObject) => onClose(pathObject)}
					onSave={(data) => onSave(data)}
				/>
			</FormContainer>
		</>
    );

};

export default Form;
